import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import usePresenca from '../hooks/usePresenca';
import logo from '../assets/logo-fio.svg';
import { avisarBadge } from '../helpers/appNativo';
import { TEMPO_DE_POLL_MS, telaVisivel } from '../helpers/rede';

// Cor/rótulo da disponibilidade do atendente.
const PRESENCA_UI = {
  disponivel: { cor: '#2fb344', label: 'Disponível' },
  ausente: { cor: '#f5a623', label: 'Ausente' },
  offline: { cor: '#8a8a8a', label: 'Offline' },
};

// Modulos agrupados por DOMINIO. Cada modulo concentra operacao + admin do
// proprio dominio. "Administracao" sobra apenas para meta-config do portal
// (usuarios e perfis).
export const MODULES = [
  {
    key: 'atendimento',
    label: 'Atendimento',
    icon: 'bi-chat-dots',
    items: [
      {
        to: '/ti/atendimentos',
        label: 'Conversas',
        icon: 'bi-chat-left-text',
        description: 'Conversas WhatsApp dos usuários',
        permissao: 'ti:atendimentos',
      },
      {
        to: '/ti/painel',
        label: 'Painel',
        icon: 'bi-speedometer2',
        description: 'Painel ao vivo do gestor — filas, espera e quem atende',
        permissao: 'ti:dashboard',
        permissaoAlternativa: 'admin:atendimento',
      },
      {
        to: '/ti/atendimentos/indicadores',
        label: 'Indicadores',
        icon: 'bi-graph-up',
        description: 'KPIs de atendimento, TTFR/TTR e SLA por período',
        permissao: 'ti:indicadores',
        permissaoAlternativa: 'admin:atendimento',
      },
      {
        to: '/ti/contatos',
        label: 'Contatos',
        icon: 'bi-person-vcard',
        description: 'Cadastro de contatos para iniciar conversas outbound',
        permissao: 'ti:atendimentos',
      },
      {
        to: '/admin/whatsapp',
        label: 'Canais WhatsApp',
        icon: 'bi-whatsapp',
        description: 'Canais de atendimento (números conectados)',
        permissao: 'admin:whatsapp',
        permissaoAlternativa: 'canal:conectar',
      },
      {
        to: '/admin/fluxos',
        label: 'Fluxos do Bot',
        icon: 'bi-diagram-3',
        description: 'Construtor de fluxos de atendimento por canal',
        permissao: 'admin:fluxos',
      },
      {
        to: '/admin/atendimento',
        label: 'Configurações',
        icon: 'bi-sliders',
        description: 'Horário, mensagens, SLA, catálogos',
        permissao: 'admin:atendimento',
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administração',
    icon: 'bi-gear',
    items: [
      {
        to: '/admin/usuarios',
        label: 'Usuários',
        icon: 'bi-person-gear',
        description: 'Gerenciar usuários do portal',
        permissao: 'admin:usuarios',
      },
      {
        to: '/admin/perfis',
        label: 'Perfis',
        icon: 'bi-shield-lock',
        description: 'Gerenciar perfis e permissões',
        permissao: 'admin:perfis',
      },
    ],
  },
];

const ALL_ITEMS = MODULES.flatMap((m) => m.items);

export default function Sidebar() {
  const { user, logout, temPermissao } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // No mobile, fecha o drawer sempre que a rota muda (qualquer navegação —
  // NavLink, botão Configurações, etc). No desktop é no-op.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Recolhimento fixo (persistido) + espiada temporaria no hover (peek).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [peeking, setPeeking] = useState(false);
  const peekTimer = useRef(null);

  // Espelha o estado fixo numa classe do body para o .main-content reagir
  // (empurrar conteudo) sem precisar elevar o estado ate o App.
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  const toggleCollapsed = () => {
    setPeeking(false);
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Peek com delay de intencao: so expande temporariamente se o mouse ficar.
  const handlePeekEnter = () => {
    if (!collapsed) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeeking(true), 320);
  };
  const handlePeekLeave = () => {
    clearTimeout(peekTimer.current);
    setPeeking(false);
  };
  useEffect(() => () => clearTimeout(peekTimer.current), []);

  const [naoLidas, setNaoLidas] = useState(0);
  const { status: presenca, ausente, podeAtender: podeAtenderPresenca, toggleAusente } = usePresenca();

  const podeAtendimentos = temPermissao('ti:atendimentos');

  useEffect(() => {
    if (!podeAtendimentos) {
      setNaoLidas(0);
      return undefined;
    }
    let ativo = true;
    let emVoo = false; // ver helpers/rede: não empilha, não roda com a tela escondida
    const buscar = async () => {
      if (emVoo || !telaVisivel()) return;
      emVoo = true;
      try {
        const res = await axios.get(`${API}/atendimentos/nao-lidas/total`, { timeout: TEMPO_DE_POLL_MS });
        if (!ativo) return;
        const total = res.data?.data?.total || 0;
        setNaoLidas(total);
        // Mesmo número, sem consulta nova: no app ele vira a bolinha do ícone.
        avisarBadge(total);
      } catch (e) { /* silencioso - badge e nice-to-have */ }
      finally { emVoo = false; }
    };
    buscar();
    const id = setInterval(buscar, 10000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [podeAtendimentos]);

  const visibleModules = useMemo(
    () => MODULES.map((mod) => ({
      ...mod,
      items: mod.items.filter((item) => {
        const liberado = item.permissao && temPermissao(item.permissao);
        const alternativa = item.permissaoAlternativa && temPermissao(item.permissaoAlternativa);
        if (item.permissao && !liberado && !alternativa) return false;
        return true;
      }),
    })).filter((mod) => mod.items.length > 0),
    [user]
  );

  const currentSection = useMemo(
    () => ALL_ITEMS.find((item) => location.pathname.startsWith(item.to)),
    [location.pathname]
  );

  // `logout` virou assíncrono: ele solta o aviso deste aparelho no servidor
  // ANTES de limpar o token (senão não haveria como se autenticar). Navegar sem
  // esperar levaria pro /login com a sessão ainda de pé, e a rota devolveria a
  // pessoa pra dentro por um instante.
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigate = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <header className="mobile-header">
        <button type="button" onClick={() => setMobileOpen((prev) => !prev)} aria-label="Abrir menu">
          <i className={`bi ${mobileOpen ? 'bi-x-lg' : 'bi-list'}`} />
        </button>
        <img src={logo} alt="Centrão" className="sidebar-logo" />
        <div className="sidebar-title">
          Fio
          <span>{currentSection?.label || 'Fio'}</span>
        </div>
      </header>

      <div className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

      <aside
        className={`sidebar sidebar--model ${mobileOpen ? 'open' : ''} ${collapsed && !peeking ? 'sidebar--collapsed' : ''}`}
        onMouseEnter={handlePeekEnter}
        onMouseLeave={handlePeekLeave}
      >
        <div className="sidebar-header">
          <img src={logo} alt="Centrão Materiais de Construção" className="sidebar-logo" />
          <div className="sidebar-title">
            Fio
            <span>Centrão</span>
          </div>
        </div>

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <i className="bi bi-chevron-left" />
        </button>

        <div className="sidebar-nav">
          {visibleModules.map((mod) => {
            const renderItem = (item) => {
              const mostraBadge = item.to === '/ti/atendimentos' && naoLidas > 0;
              return (
                <NavLink key={item.to} to={item.to} onClick={handleNavigate} data-tooltip={item.label}>
                  <span className="sidebar-nav__icon-wrap">
                    <i className={`bi ${item.icon}`} />
                    {mostraBadge && (
                      <span className="sidebar-nav__badge">
                        {naoLidas > 99 ? '99+' : naoLidas}
                      </span>
                    )}
                  </span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              );
            };

            // Lista achatada: grupos viram rotulos/divisorias (que somem ao
            // recolher) e os itens ficam numa lista unica — os mesmos icones
            // nos dois estados. Modulos "standalone" (Dashboard etc.) nao tem
            // rotulo.
            return (
              <div key={mod.key} className="sidebar-group">
                {!mod.standalone && (
                  <div className="sidebar-group__label">{mod.label}</div>
                )}
                {mod.items.map(renderItem)}
              </div>
            );
          })}

          <div className="sidebar-section">Operação</div>
          <div className="sidebar-info-card">
            <strong>{currentSection?.label || 'Fio'}</strong>
            <div>
              {currentSection?.description || 'Central de atendimento e chatbot do Centrão.'}
            </div>
          </div>
        </div>

        <div className="sidebar-footer sidebar-footer--model">
          <div className="sidebar-user-block">
            <div className="sidebar-user-line">
              <i className="bi bi-person-circle" />
              <div>
                <strong>{user?.nome || 'Operador'}</strong>
                <span>{user?.usuario || 'sem identificação'}</span>
              </div>
            </div>
            {podeAtenderPresenca && (() => {
              const soDot = collapsed && !peeking; // recolhido e sem hover = só a bolinha, centrada
              return (
                <button
                  type="button"
                  onClick={toggleAusente}
                  title={ausente ? 'Você está Ausente (não recebe conversa nova). Clique para ficar Disponível.' : 'Você está Disponível. Clique para entrar em Ausente (pausa/almoço).'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, width: '100%', marginTop: 8,
                    justifyContent: soDot ? 'center' : 'flex-start',
                    padding: soDot ? '6px' : '6px 10px', borderRadius: 8, cursor: 'pointer',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'inherit', fontSize: '0.8rem',
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: (PRESENCA_UI[presenca] || PRESENCA_UI.offline).cor }} />
                  {!soDot && (
                    <>
                      <span style={{ flex: 1, textAlign: 'left' }}>{(PRESENCA_UI[presenca] || PRESENCA_UI.offline).label}</span>
                      <i className={`bi ${ausente ? 'bi-pause-circle' : 'bi-toggle-on'}`} style={{ opacity: 0.6, fontSize: '1rem' }} />
                    </>
                  )}
                </button>
              );
            })()}
          </div>

          <div className="sidebar-footer-actions">
            <button type="button" className="sidebar-footer-link" onClick={() => navigate('/ti/configuracoes')}>
              <i className="bi bi-gear" />
              <span>Configurações</span>
            </button>
            <button type="button" className="sidebar-footer-link" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
